import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { Agency } from 'src/app/models/Agency';
import { PaginatedResult } from 'src/app/shared/model/paginated-result';

import { ToastService } from './../../shared/components/toast/toast.service';
import { AgencyService } from './../../shared/services/agency.service';
import { UserCommission } from 'src/app/models/UserCommission';
import { AgencyFilter } from 'src/app/models/AgencyFilter';

/**
 * Ordens que o back aplica (`AgencyController@index`, whitelist). "Saldo" saiu de
 * propósito: o back nunca a aplicou (reordenava só a página), e o mockup aprovado
 * em 27/10 (aba "Agências (admin)") ficou com Comissão pendente / Nome.
 */
export type AgencyOrder = 'pending' | 'name';
const AGENCY_ORDERS: AgencyOrder[] = ['pending', 'name'];

@Component({
  selector: 'app-partner-management',
  templateUrl: './partner-management.component.html',
  styleUrls: ['./partner-management.component.scss']
})
export class PartnerManagementComponent implements OnInit, OnDestroy {

  @ViewChild('paymentModal') modal: any;

  page = 1;
  pageSize = 10;
  filter = '';
  agencies$: Observable<PaginatedResult<Agency[]>>;
  loadingAgencies = true;
  /** Falha da lista (500 / sem resposta): aviso com "Tentar de novo", nunca "nenhuma agência". */
  agenciesError = false;

  paymentForm: UntypedFormGroup;

  subscription: Subscription;

  /** `pending` é o padrão do back quando a ordem não vem (maior comissão pendente primeiro). */
  orderBy: AgencyOrder = 'pending';
  invertedOrder = false;

  now = new Date().toISOString().slice(0, 10);

  showPendings: Agency[] = [];

  private destroyed = new Subject<void>();
  private agenciesRequest = 0;

  get orderSelectValue(): string {
    return `${this.orderBy}|${this.invertedOrder ? 1 : 0}`;
  }

  constructor(
    private agencyService: AgencyService,
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.paymentForm = this.fb.group({
      id: [null],
      amount: [null, [Validators.required]],
      period: [null, [Validators.required]],
      payment_date: [this.now, [Validators.required]],
      description: [null]
    });

    this.route.queryParams.pipe(takeUntil(this.destroyed)).subscribe(params => {
      this.page = +params.page || 1;
      this.filter = params.filter || '';
      this.pageSize = +params.per_page || this.pageSize;
      // URL salva com `?orderBy=credits` (a opção "Saldo" antiga) ou lixo cai no
      // padrão — o select não fica em branco e o back não recebe ordem desconhecida.
      this.orderBy = AGENCY_ORDERS.includes(params.orderBy) ? params.orderBy : 'pending';
      this.invertedOrder = params.invertedOrder === true || params.invertedOrder === 'true';
      this.listAgencies();
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  alternateHiddenStatus(agency: Agency) {
    if (this.showPendings.includes(agency)) {
      this.showPendings = this.showPendings.filter(ag => ag !== agency);
    } else {
      this.showPendings.push(agency);
    }
  }

  /** Período com algum valor (pendente ou pago): sempre visível. */
  hasValues(commission: UserCommission): boolean {
    return this.getPendingAmount(commission) !== 0 || this.getPaidAmount(commission) !== 0;
  }

  /** Períodos zerados ficam recolhidos atrás de "mostrar N períodos zerados". */
  zeroedCount(agency: Agency): number {
    return agency.commissions.filter(commission => !this.hasValues(commission)).length;
  }

  initials(name: string): string {
    return (name || '')
      .split(' ')
      .filter(part => part)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('');
  }

  listAgencies() {
    const filter: AgencyFilter = this.filter ? { text: this.filter } : null;
    const request = ++this.agenciesRequest;
    const isCurrent = () => request === this.agenciesRequest;

    this.loadingAgencies = true;
    this.agenciesError = false;
    this.agencies$ = this.agencyService.listAgencies(this.page, this.pageSize, filter, this.orderBy, this.invertedOrder)
      .pipe(
        tap(() => {
          if (isCurrent()) {
            this.loadingAgencies = false;
          }
        }),
        catchError(() => {
          if (isCurrent()) {
            this.agenciesError = true;
          }

          return EMPTY;
        }),
        finalize(() => {
          if (isCurrent()) {
            this.loadingAgencies = false;
          }
        }),
      );
  }

  showStatus(commission: UserCommission) {
    return commission.status === 'pending' ? 'Pendente' : 'Pago';
  }

  getPendingAmount(commission: UserCommission) {
    const paidAmount = this.getPaidAmount(commission);
    return commission.amount - paidAmount;
  }

  getPaidAmount(commission: UserCommission) {
    return commission.payments.reduce((i, p) => i + Number(p.amount), 0);
  }

  /** Um select só ("Comissão pendente (maior primeiro)", "Nome Z→A"…) no lugar de select + "Reverso". */
  orderChanged(value: string): void {
    const [order, inverted] = value.split('|');
    this.orderBy = AGENCY_ORDERS.includes(order as AgencyOrder) ? order as AgencyOrder : 'pending';
    this.invertedOrder = inverted === '1';
    this.pageChanged(1);
  }

  pageChanged(page: number) {
    const queryParams: Record<string, any> = {
      page,
      per_page: this.pageSize,
      filter: this.filter,
      invertedOrder: this.invertedOrder,
      orderBy: this.orderBy,
    };
    if (!this.filter) {
      delete queryParams.filter;
    }
    this.router.navigate(['/partner-management'], { queryParams });
  }

  register(agency: Agency, commission: UserCommission) {
    this.paymentForm.reset();
    this.now = new Date().toISOString().slice(0, 10);
    this.paymentForm.patchValue({
      id: commission.id,
      amount: this.getPendingAmount(commission),
      period: commission.period,
      payment_date: this.now
    });

    this.modalService.open(this.modal).result.then(btn => {
      if (btn === 'YES') {
        const payment = this.paymentForm.getRawValue();
        this.subscription = this.agencyService.savePayment(agency, payment)
          .subscribe(() => {
            this.toastService.success('Pagamento incluído com sucesso!');
            this.listAgencies();
          },
            e => this.toastService.error('Erro ao cadastrar pagamento.'));
      }
    }, () => undefined);
  }

  save(modal: any) {
    const payment = this.paymentForm.getRawValue();
    const actualDate = new Date();
    const paymentDate = new Date(payment.payment_date);
    if (paymentDate > actualDate) {
      this.toastService.error('A Data do Pagamento não pode ser maior que a data atual.');
      return;
    }

    modal.close('YES');
  }
}
